/*
 * Public API Surface
 */
export * from './lib/components/collapse-all/collapse-all.component';
export * from './lib/components/export-limits/export-limits.component';
export * from './lib/components/filter-bar-label/filter-bar-label.component';
export * from './lib/components/inputs/base-input';
export * from './lib/components/inputs/text-input';
export * from './lib/components/inputs/text/text.component';
export * from './lib/components/simple-select/simple-select.component';
export * from './lib/directives/avoid-copy-paste/avoid-copy-paste';
export * from './lib/directives/set-title/title.directive';
export * from './lib/utils/show-on-invalid-error-state-matcher';
export * from './lib/components/global-selection/global-selection.component';
export * from './lib/components/goes-to-record/goes-to-record.component';
export * from './lib/components/location-permissions/location-permissions.component';
export * from './lib/components/miq-checkbox-multigroup/checkboxgroup/checkboxgroup.component';
export * from './lib/components/miq-checkbox-multigroup/group';
export * from './lib/components/miq-checkbox-multigroup/miq-checkbox-multigroup-component';
export * from './lib/components/miq-checkbox-multigroup/miq-checkbox-multigroup-parent';
export * from './lib/components/multiselectcheckbox/multiselectcheckbox.component';
export * from './lib/components/records-permissions/records-permissions.component';
export * from './lib/components/sub-selection/sub-selection.component';
export * from './lib/components/table-paginator/table-paginator.component';
export * from './lib/dialogs/add-location/add-location.component';
export * from './lib/dialogs/confirm-dialog/confirm-dialog.component';
export * from './lib/dialogs/copy-user/copy-user.component';
export * from './lib/dialogs/export-limits-dialog/export-limits-dialog.component';
export * from './lib/dialogs/record-permission-dialog/record-permission-dialog.component';
export * from './lib/directives/only-numbers.directive';
export * from './lib/directives/stop-click.directive';
export * from './lib/pipes/array-join.pipe';
export * from './lib/pipes/hide-flagged-items.pipe';
export * from './lib/pipes/moment-timezone.pipe';

/*
 * L2-R6: UI (components / pipes / directives) and one dialog-opening service promoted out of
 * src/app so the host and the reports remote import ONE copy instead of the remote bundling its
 * own. Each old host path is now a re-export shim of the module listed here.
 */
export * from './lib/components/bg-image/bg-image.component';
/*
 * L2-S1/T7: two more reports-remote seams. `CreateSelectFoldersComponent` had zero blockers;
 * `CommentsComponent` needed its `CurrentRecordState` snapshot turned into optional inputs first
 * (see the class comment there).
 */
export * from './lib/components/create-select-folders/create-select-folders.component';
export * from './lib/components/comments/comments.component';
export * from './lib/components/comments/comments-api.service';
export * from './lib/components/comments/entry-comment/entry-comment.component';
export * from './lib/components/search/search.component';
export * from './lib/components/chart-render/chart-render.component';
export * from './lib/components/contact-card/contact-card.component';
export * from './lib/components/go-back-link/go-back-link.component';
export * from './lib/components/pinned-contact-card/pinned-contact-card.component';
export * from './lib/components/search-bar/search-bar.component';
export * from './lib/dialogs/edp-edit-failed/edp-edit-failed.component';
export * from './lib/dialogs/outdated-records-alert/outdated-records-alert.component';
// L2-S13: opened by TimetrackingService (moving to @miq/platform) through the
// SESSION_EXPIRY_DIALOG token; both shells provide the token with this class.
export * from './lib/dialogs/session-expiry-dialog/session-expiry-dialog.component';
export * from './lib/directives/editing-box-target.directive';
export * from './lib/pipes/contextualized.pipe';
export * from './lib/pipes/date-with-format.pipe';
export * from './lib/pipes/replace-placeholders.pipe';
export * from './lib/pipes/to-class.pipe';
export * from './lib/services/deactivate-dialog.service';
export * from './lib/utils/member-state';
export * from './lib/wizard/availability-wizard.config';
export * from './lib/wizard/model/wizard-overlay-config';
export * from './lib/wizard/model/wizard.model';
export * from './lib/wizard/wizard-driver.service';
export * from './lib/wizard/wizard-manager.service';
export * from './lib/wizard/wizard-projector.directive';
export * from './lib/wizard/wizard.component';
export * from './lib/wizard/wizard.constants';

/*
 * L2-R7: Material-facing remote scaffold + the biggest remaining reports<->host template coupling.
 * Each old host path is now a re-export shim of the module listed here.
 */
export * from './lib/adapters/miq-date-adapter.service';
export * from './lib/pipes/data-cell-formatter.pipe';

/*
 * L2-S4: the Excel/CSV export slice. `ExcelSharedService` and the catalog measurement formatters
 * moved out of src/app so the reports remote's report-detail dataview service stops importing the
 * host. Each old host path is now a re-export shim of the module listed here.
 */
export * from './lib/catalog/measurement-formatters';
export * from './lib/excel/excel.constants';
export * from './lib/excel/excel-export.helpers';
export * from './lib/excel/excel-shared.service';

/*
 * L2-S5: the tree-data-table family — the reports remote's report list renders it, and six host
 * complex-table components render `CommandsComponent` on its own. Each old
 * `@shared/components/tree-data-table/**` path is now a re-export shim, as are the two PrimeNG
 * checkbox subclasses in `@shared/vendors/**` that the table's header and rows render.
 *
 * The two small `lib/utils` modules exist only to invert edges this family had into host code that
 * must NOT follow it here: `makeSimpleFormGroup` is the pure half of the host's
 * `SimpleFormGroupService` (an HTTP service), and `navigateToEntity` is a dependency-free
 * `window.open` that used to live in `@records/.../tim-availability.constants`.
 */
export * from './lib/components/tree-data-table/tree-data-table.component';
export * from './lib/components/tree-data-table/tree-data-row/tree-data-row.component';
export * from './lib/components/tree-data-table/tree-data-row/indent.directive';
export * from './lib/components/tree-data-table/tree-data-row/subtitle-cell-formater.pipe';
export * from './lib/components/tree-data-table/commands/commands.component';
export * from './lib/vendors/table-checkbox.component';
export * from './lib/vendors/table-header-checkbox.component';
export * from './lib/utils/simple-form-group';
export * from './lib/utils/navigate-to-entity';

/*
 * L2-S5: the ag-grid wrapper — the reports remote's single heaviest remaining seam (four corporate
 * report tables render it). Its two module-eval couplings to the host are inverted into
 * @miq/platform tokens: `AG_GRID_LICENSE_KEY` (was `environment.agGridEnterpriseKey`) and
 * `AG_CELL_COMPONENTS` (was the ~22-component `MiqAgCellRenderers` / `MiqAgCellEditors` maps in
 * `@shared/components/table/ag-table-grid-options.ts`, which stay in the host). Each old host path
 * is now a re-export shim.
 */
export * from './lib/table/ag-table.component';
export * from './lib/table/ag-scrollbar.directive';
export * from './lib/table/ag-cell-renderer-map';
export * from './lib/directives/stop-keydown.directive';
export * from './lib/catalog/advanced-input-factory';

/*
 * L2-S5: the `filters-bar` family. `FiltersBarComponent` is rendered by records, efficiencies AND
 * the reports remote, and used to drag 142 host files into every bundle that contained it — through
 * a `view-spaces-from-tim/filters.ts` import CYCLE, `FilterOptionService`'s `RecordsSearchStateService`
 * dependency, `SharedService` and `ViewSpacesFromTimService`. Those four roots were cut first (see
 * FILTER_SEARCH_DRIVER / TIM_FILTERS_SINK / MORE_FILTERS_DIALOG in @miq/platform's tokens.ts and
 * `SharedApiService.getMarketQtd`), which left the family reaching nothing but libraries, so it can
 * live here.
 *
 * Everything below keeps a re-export shim at its old `@shared/...` / `@records/...` path, because
 * the family has many host importers. `range-item` + `ce-range-item` + `range-item.helper` are
 * mutually recursive and therefore moved as one unit, as did the whole `quarter-selector` tree that
 * `period-range` renders.
 */
export * from './lib/components/filters-bar/filters-bar.component';
export * from './lib/components/filters-bar/filters.utils';
export * from './lib/components/filters-bar/simple-form-group.service';
export * from './lib/components/filters-bar/period-range/period-range.component';
export * from './lib/components/filters-bar/equals-filter/equals-filter.component';
export * from './lib/components/filters-bar/auto-complete/auto-complete.component';
export * from './lib/components/range-item/range-item.component';
export * from './lib/components/range-item/ce-range-item.component';
export * from './lib/components/range-item/range-item.helper';
export * from './lib/components/range-button/range-button.constants';
export * from './lib/components/range-button/range-button.enum';
export * from './lib/components/range-button/range-button.model';
export * from './lib/components/filter-option/filter-option.component';
export * from './lib/components/filter-option/filter-option.service';
export * from './lib/components/quarter-selector/quarter-selector.component';
export * from './lib/components/quarter-selector/quarter-selector.constants';
export * from './lib/components/quarter-selector/range-quarter-selector.component';
export * from './lib/components/quarter-selector/range-quarter-selector.service';
export * from './lib/components/quarter-selector/quarter-selector-overlay/quarter-selector-overlay.component';
export * from './lib/components/quarter-selector/quarter-selector-overlay/quarter-selector-overlay.model';
export * from './lib/components/quarter-selector/quarter-selector-overlay/quarter-selector.state';
export * from './lib/utils/valid-filters';

/**
 * L2-S11: MoreFiltersComponent (the v1 more-filters/more-columns dialog body) plus the two leaf
 * modules it owns. Moved so the HOST and the reports remote share ONE copy through the federation
 * singleton instead of each bundling its own — that retires the
 * `@shared/dialogs/more-filters/more-filters.component` entry from
 * projects/reports-remote/host-import-allowlist.json.
 *
 * The last host root it reached, `RecordsSearchStateService`, is now taken through
 * FILTER_SEARCH_DRIVER (@miq/platform tokens.ts), which gained one optional member —
 * `setDefaultSortAndColumns` — for the `popupFrom === 'record-list'` branch. Four records-domain
 * constants (POPUP_TYPE, defaultIDs, FILTER_EMPTY_VALUE, SPACE_TENANCY_COMMENTS_COLUMNS) are
 * inlined in the component; see the comments there for why they cannot be imported.
 *
 * `space-default-filters.config` moved with it (its only import is SpaceColumns from @miq/catalog)
 * and keeps a shim at its old @records path for its records importers.
 *
 * DELIBERATELY NOT re-exported here: the model symbols the component consumes (`filterTypes`,
 * `MoreFiltersData`, `MoreFiltersOptions`, `MUTUALLY_EXCLUSIVE_FILTERS`) live in
 * @miq/shared-models. Re-exporting `filterTypes` from this entry point would collide with the
 * UNRELATED `filterTypes` string map already exported above from
 * `lib/components/filters-bar/filters.utils` — a new silent duplicate pair. Consumers that need
 * those types import them from '@miq/shared-models'; only the HOST shim re-exports them.
 */
export * from './lib/dialogs/more-filters/more-filters.component';
export * from './lib/dialogs/more-filters/more-filters.helpers';
export * from './lib/dialogs/more-filters/space-default-filters.config';
export * from './lib/dialogs/more-filters/more-filters.config';
